import { Box, Paper, useMediaQuery } from "@material-ui/core";
import Highcharts from "highcharts";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import CssGroupButton from "src/components/Button/CssGroupButton";
import { STAKING_TYPE } from "src/components/ScoringChart/Stack/config";
import StackChart from "src/components/ScoringChart/Stack/StackChart";
import { baseBeautifulTooltip } from "src/components/ScoringChart/tool-utils/BeautifulTooltip";
import DateGroupButton from "src/components/ScoringChart/tool-utils/DateGroupButton";
import { NoteTypography } from "src/components/Typography/TitleTypography";
import DetailMonitoringWrapper from "src/components/Wrapper/DetailMonitoringWrapper";
import { MONITORING } from "src/config/constants";
import {
  useAnalysisOverviewChartSelector,
  useAnalysisOverviewSelector,
} from "src/context/monitoring/analysisOverviewContext";
import { numberWithCommas, toFixed } from "src/services/utils";

const dateOption = [
  { label: "1D", number: 24, interval: 1 },
  { label: "1W", number: 84, interval: 2 },
  { label: "1M", number: 90, interval: 8 },
  { label: "3M", number: 180, interval: 12 },
];

const getOption = () => {
  return {
    chart: {
      height: 500,
    },
    legend: {
      enabled: true,
    },
    ...baseBeautifulTooltip(
      (value) => `$${numberWithCommas(toFixed(value, 2))}`,
      true,
      (value) => {
        const times = value.split("-");
        let time1 = times[0].length === 0 ? null : Highcharts.dateFormat("%b %e, %Y, %k:%M", Number(times[0]));
        let time2 = Highcharts.dateFormat("%b %e, %Y, %k:%M", Number(times[1]));
        let result = time1 ? `<div>From: ${time1}</div>` : "";
        result += `<div>To: ${time2}</div>`;
        return result;
      }
    ),
    xAxis: {
      labels: {
        enabled: true,
        formatter: function () {
          const times = this.value.split("-");
          let time2 = Highcharts.dateFormat("%e.%b", Number(times[1]));
          return time2;
        },
      },
    },
  };
};

export default function TransactionVolume() {
  const { pool } = useParams();
  const match = useMediaQuery((theme) => theme.breakpoints.only("xs"));
  const { fetchTransactionVolume } = useAnalysisOverviewSelector((state) => state.fetch);
  const transactionVolume = useAnalysisOverviewChartSelector(MONITORING.OVERVIEW.transactionVolume, pool);
  const [stakingType, setStakingType] = useState(STAKING_TYPE.PERCENT);

  useEffect(() => {
    if (transactionVolume?.active === undefined)
      fetchTransactionVolume(pool, { number: 90, interval: 8 }, { active: 2 });
    else fetchTransactionVolume(pool, transactionVolume?.metadata, { active: transactionVolume?.active });
  }, [transactionVolume?.active, pool]);

  const { categories, series, isLoading } = useMemo(() => {
    if (transactionVolume) {
      return {
        isLoading: false,
        categories: [].concat(transactionVolume["timestamps"]),
        series: [
          {
            name: "deposit",
            data: transactionVolume["deposit"].map((element) => {
              return [].concat(element);
            }),
          },
          {
            name: "borrow",
            data: transactionVolume["borrow"].map((element) => {
              return [].concat(element);
            }),
          },
          {
            name: "withdraw",
            data: transactionVolume["withdraw"].map((element) => {
              return [].concat(element);
            }),
          },
          {
            name: "repay",
            data: transactionVolume["repay"].map((element) => {
              return [].concat(element);
            }),
          },
        ],
      };
    }
    return {
      isLoading: true,
      categories: [],
      series: [],
    };
  }, [transactionVolume]);

  return (
    <DetailMonitoringWrapper
      navigation={{ text: "overview", name: "transaction volume", link: `/overview-analysis/${pool}` }}
      selectLink="/overview-analysis/transaction-volume"
      Component={{
        Description: (
          <>
            <NoteTypography>
              The chart compares the total daily transaction volume of Deposit, Borrow, Withdraw and Repay transactions
              in a lending pool. This comparison not only shows the amount of money invested in the lending pool but
              also indicates the investment trends over time. For example, a significant increase in the deposit implies
              that users tend to invest their assets in the lending pool.
            </NoteTypography>
            <NoteTypography>
              Users can use the USD unit to monitor the activeness of the lending market, or they can use the percentage
              unit to observe the dominated type of transactions.
            </NoteTypography>
          </>
        ),
      }}
    >
      <Paper>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" mb={2}>
          <CssGroupButton
            fullWidth={match}
            style={match ? { marginBottom: "0.5rem" } : {}}
            defaultActive={stakingType === STAKING_TYPE.PERCENT ? 0 : 1}
            config={[
              { label: "Percentage(%)", onClick: () => setStakingType(STAKING_TYPE.PERCENT) },
              { label: "Value", onClick: () => setStakingType(STAKING_TYPE.NORMAL) },
            ]}
          />
          <DateGroupButton
            fullWidth={match}
            defaultActive={transactionVolume?.active ?? 0}
            picker={{
              continueClick: (from, to, active) => {
                fetchTransactionVolume(pool, { from, to }, { active });
              },
            }}
            config={dateOption.map((item, _) => ({
              data: item,
              onClick: (data, active) => {
                fetchTransactionVolume(pool, { number: data.number, interval: data.interval }, { active });
              },
            }))}
          />
        </Box>
        <StackChart
          isLoading={isLoading}
          series={series}
          categories={categories}
          option={getOption()}
          type={stakingType}
        />
      </Paper>
    </DetailMonitoringWrapper>
  );
}
